import React, { createContext, useEffect, useMemo } from 'react'

import { serverSideQuery, useQuery } from '@/api'
import { LISTINGS_ENDPOINT } from '@/constants'

import {
    composedListingsSelector,
    globalDataSelector,
    globalPriceRangeObjectSelector,
    hasListingsSelector,
    isFaceValueSelector,
    listingsInSelectedSectionSelector,
    mapJsonFileNameSelector,
    seatViewObjectSelector,
    showAipSelector,
    i18nListingsDataSelector,
    mapboxMapLayoutUrl,
} from './selectors'
import type {
    ListingsContextType,
    ListingsParamsType,
    ListingsProviderProps,
    ListingsResponse,
    ListingsSelectorsType,
    ListingsStateType,
} from './types'
import { removeExpiredRecentlyViewedFromLocalStorage } from './utils'

const defaultContextValue: ListingsContextType = {
    state: {},
    selectors: {
        composedListings: [],
        seatViewObject: undefined,
        globalPriceRangeObject: {
            lowestPrice: 0,
            medianPrice: 0,
            highestPrice: 0,
            lowestAllInPrice: 0,
            medianAllInPrice: 0,
            highestAllInPrice: 0,
        },
        mapJsonFileName: '',
        isFaceValue: false,
        showAip: false,
        hasListings: false,
        i18nListingsData: undefined,
        mapboxLayoutUrl: '',
        globalDataSelector: undefined,
        listingsInSelectedSection: () => [],
    },
    fetching: true,
}

export const getListingsSelectors = (state: ListingsStateType): ListingsSelectorsType => ({
    composedListings: composedListingsSelector(state),
    seatViewObject: seatViewObjectSelector(state),
    globalPriceRangeObject: globalPriceRangeObjectSelector(state),
    mapJsonFileName: mapJsonFileNameSelector(state),
    isFaceValue: isFaceValueSelector(state),
    showAip: showAipSelector(state),
    hasListings: hasListingsSelector(state),
    i18nListingsData: i18nListingsDataSelector(state),
    mapboxLayoutUrl: mapboxMapLayoutUrl(state),
    globalDataSelector: globalDataSelector(state),
    // TODO: consider making this a dispatch instead of a parametrized selector
    listingsInSelectedSection: (sectionId?: string) => listingsInSelectedSectionSelector(state, sectionId),
})

const ListingsContext = createContext<ListingsContextType>(defaultContextValue)
const { Provider } = ListingsContext

export const ListingsProvider: React.FC<ListingsProviderProps> = ({ children, initialParams, initialProps }) => {
    const isServerSide = typeof document === 'undefined'

    useEffect(() => {
        if (isServerSide || !localStorage) return
        removeExpiredRecentlyViewedFromLocalStorage()
    }, [isServerSide])

    const { fetching, data, error } = useQuery<ListingsResponse>({
        endpoint: LISTINGS_ENDPOINT,
        params: initialParams,
        initialFetchDisabled: !!initialProps?.data,
        initialData: initialProps?.data,
        enabled: !!initialParams.productionId,
    })

    const contextValue: ListingsContextType = useMemo(() => {
        const state: ListingsStateType = { fetching, data, error }
        const selectors: ListingsSelectorsType = getListingsSelectors(state)
        return { state, selectors, fetching }
    }, [fetching, data, error])

    return <Provider value={contextValue}>{children}</Provider>
}

export const serverSideRead = async (params: ListingsParamsType, maxAgeMs?: number): Promise<ListingsResponse> => {
    const queryInstance = serverSideQuery<ListingsResponse>(LISTINGS_ENDPOINT)
    const listingsResponse: ListingsResponse = await queryInstance(params, maxAgeMs)
    return listingsResponse
}

export default ListingsContext
